import { useMemo } from 'react';

import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

import { usePathname } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';

import { allLangs } from 'src/locales';
import { _contacts, _notifications } from 'src/_mock';
import { varAlpha, stylesMode } from 'src/theme/styles';

import { bulletColor } from 'src/components/nav-section';
import { useSettingsContext } from 'src/components/settings';

import { hasUserAccess } from 'src/auth/context/jwt';

import { Main } from './main';
import { NavMobile } from './nav-mobile';
import { layoutClasses } from '../classes';
import { NavVertical } from './nav-vertical';
import { NavHorizontal } from './nav-horizontal';
import { _account } from '../config-nav-account';
import { HeaderBase } from '../core/header-base';
import { _workspaces } from '../config-nav-workspace';
import { LayoutSection } from '../core/layout-section';

// ----------------------------------------------------------------------

// Drop nav nodes the current user can't access (policy mirror of the JWT — purely
// cosmetic; route guards + the backend 403 are the real enforcement). A node is
// hidden when its `policies` aren't satisfied; a parent is hidden when none of its
// children survive. Nodes with no `policies` are visible to any authenticated user.
const filterNavItems = (items = []) =>
  items.reduce((acc, item) => {
    if (item.policies?.length && !hasUserAccess(item.policies)) {
      return acc;
    }
    if (item.children) {
      const children = filterNavItems(item.children);
      if (!children.length) {
        return acc;
      }
      acc.push({ ...item, children });
      return acc;
    }
    acc.push(item);
    return acc;
  }, []);

const filterNavByAccess = (nav = []) =>
  nav
    .map((group) => ({ ...group, items: filterNavItems(group.items ?? []) }))
    .filter((group) => group.items.length);

// ----------------------------------------------------------------------

// Dabang's drawer item is a rounded pill, and the selected one is filled solid with
// `primary.main` and a white label — not the 8%-alpha tint the nav section defaults to. Radius
// is held at 10px rather than the template's 16: this sidebar carries far more entries than
// Dabang's eight, and a full pill at that count reads as a stack of lozenges.
const NAV_SHAPE_VARS = {
  '--nav-item-radius': '10px',
  '--nav-item-pl': '12px',
  '--nav-item-pr': '12px',
};

// The app bar owns the page title now, so it reads the name off the nav rather than each view
// passing one down. Deepest match wins, which keeps details routes
// (`/game-configuration/:id`) titled by their section ("Games") instead of by a shorter
// sibling that happens to prefix-match.
const findPageTitle = (nav = [], pathname = '') => {
  let match = { title: '', length: 0 };

  const visit = (items = []) => {
    items.forEach((item) => {
      const isMatch =
        item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`));

      if (isMatch && item.path.length > match.length) {
        match = { title: item.title, length: item.path.length };
      }

      if (item.children) {
        visit(item.children);
      }
    });
  };

  nav.forEach((group) => visit(group.items));

  return match.title || titleFromPathname(pathname);
};

// Not every route is in the nav — the account and profile pages aren't — and those views no
// longer draw a heading of their own, so the bar falls back to the deepest readable segment.
// Ids are skipped rather than shown as a title.
const isReadableSegment = (part) =>
  /[a-z]/i.test(part) && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(part);

const titleFromPathname = (pathname = '') => {
  const segment = pathname
    .split('/')
    .filter((part) => part !== 'dashboard' && isReadableSegment(part))
    .pop();

  return segment
    ? segment.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    : '';
};

// ----------------------------------------------------------------------

export function DashboardLayout({ sx, children, data }) {
  const theme = useTheme();

  const mobileNavOpen = useBoolean();

  const settings = useSettingsContext();

  const navColorVars = useNavColorVars(theme, settings);

  const layoutQuery = 'lg';

  const pathname = usePathname();

  const navData = useMemo(() => filterNavByAccess(data?.nav ?? []), [data?.nav]);

  const pageTitle = useMemo(() => findPageTitle(navData, pathname), [navData, pathname]);

  const isNavMini = settings.navLayout === 'mini';

  const isNavHorizontal = settings.navLayout === 'horizontal';

  const isNavVertical = isNavMini || settings.navLayout === 'vertical';

  return (
    <>
      <NavMobile
        data={navData}
        open={mobileNavOpen.value}
        onClose={mobileNavOpen.onFalse}
        cssVars={navColorVars.section}
      />

      <LayoutSection
        /** **************************************
         * Header
         *************************************** */
        headerSection={
          <HeaderBase
            layoutQuery={layoutQuery}
            disableElevation={isNavVertical}
            // The scroll-offset style swaps in a translucent `background.default` blur and
            // shrinks the bar on scroll. The bar is a fixed 64px band the page title lives in,
            // so it stays put.
            disableOffset
            onOpenNav={mobileNavOpen.onTrue}
            data={{
              nav: navData,
              langs: allLangs,
              account: _account,
              contacts: _contacts,
              workspaces: _workspaces,
              notifications: _notifications,
            }}
            slotsDisplay={{
              signIn: false,
              purchase: false,
              helpLink: false,
              workspaces: false,
              menuButton: true,
              searchbar: true,
            }}
            slots={{
              topArea: (
                <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                  This is an info Alert.
                </Alert>
              ),
              /* -- Page title -- */
              leftAreaEnd: pageTitle ? (
                <Typography component="h1" variant="h6" noWrap sx={{ color: 'inherit' }}>
                  {pageTitle}
                </Typography>
              ) : null,
              bottomArea: isNavHorizontal ? (
                <NavHorizontal
                  data={navData}
                  layoutQuery={layoutQuery}
                  cssVars={navColorVars.section}
                />
              ) : null,
            }}
            slotProps={{
              toolbar: {
                sx: {
                  // Dabang's app bar is a white `Paper` with its heading navy on top — no
                  // colour block, no elevation. The hairline underneath is ours: the template
                  // separates the bar from the canvas by tone alone (white on #F7FAFC), which
                  // is too soft to hold a full-bleed data grid below it.
                  bgcolor: 'background.paper',
                  color: 'primary.darker',
                  boxShadow: 'none',
                  borderBottom: `1px solid ${theme.vars.palette.divider}`,
                  [`& .${iconButtonClasses.root}`]: {
                    color: 'text.secondary',
                  },
                  // Dabang's search is a pill filled with `neutral.light`, not an outlined box.
                  [`& [data-slot="searchbar"]`]: {
                    borderRadius: 999,
                    bgcolor: theme.vars.palette.grey[100],
                    '&:hover': {
                      bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
                    },
                  },
                  [`& [data-slot="logo"]`]: {
                    display: 'none',
                  },
                  [`& [data-area="right"]`]: {
                    gap: { xs: 0, sm: 0.75 },
                  },
                  ...(isNavHorizontal && {
                    bgcolor: 'var(--layout-nav-bg)',
                    [`& .${iconButtonClasses.root}`]: {
                      color: 'var(--layout-nav-text-secondary-color)',
                    },
                    [theme.breakpoints.up(layoutQuery)]: {
                      height: 'var(--layout-nav-horizontal-height)',
                    },
                    [`& [data-slot="workspaces"]`]: {
                      color: 'var(--layout-nav-text-primary-color)',
                    },
                    [`& [data-slot="logo"]`]: {
                      display: 'none',
                      [theme.breakpoints.up(layoutQuery)]: {
                        display: 'inline-flex',
                      },
                    },
                    [`& [data-slot="divider"]`]: {
                      [theme.breakpoints.up(layoutQuery)]: {
                        display: 'flex',
                      },
                    },
                  }),
                },
              },
              container: {
                maxWidth: false,
                sx: {
                  // Matches `--layout-dashboard-content-px`, so the bar's title lines up with
                  // the content below it.
                  ...(isNavVertical && { px: { [layoutQuery]: 3 } }),
                },
              },
            }}
          />
        }
        /** **************************************
         * Sidebar
         *************************************** */
        sidebarSection={
          isNavHorizontal ? null : (
            <NavVertical
              data={navData}
              isNavMini={isNavMini}
              layoutQuery={layoutQuery}
              cssVars={navColorVars.section}
              onToggleNav={() =>
                settings.onUpdateField(
                  'navLayout',
                  settings.navLayout === 'vertical' ? 'mini' : 'vertical'
                )
              }
            />
          )
        }
        /** **************************************
         * Footer
         *************************************** */
        footerSection={null}
        /** **************************************
         * Style
         *************************************** */
        cssVars={{
          ...navColorVars.layout,
          '--layout-transition-easing': 'linear',
          '--layout-transition-duration': '120ms',
          // Template dimensions: a 240px drawer collapsing to `theme.spacing(9)`, a 64px bar,
          // and a 24px content gutter (its `Container` gutter) instead of the previous 40px.
          '--layout-nav-mini-width': '72px',
          '--layout-nav-vertical-width': '240px',
          '--layout-nav-horizontal-height': '64px',
          '--layout-header-desktop-height': '64px',
          '--layout-dashboard-content-pt': theme.spacing(3),
          '--layout-dashboard-content-pb': theme.spacing(5),
          '--layout-dashboard-content-px': theme.spacing(3),
        }}
        sx={{
          [`& .${layoutClasses.hasSidebar}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
            },
          },
          ...sx,
        }}
      >
        <Main isNavHorizontal={isNavHorizontal}>{children}</Main>
      </LayoutSection>
    </>
  );
}

// ----------------------------------------------------------------------

function useNavColorVars(theme, settings) {
  const {
    vars: { palette },
  } = theme;

  return useMemo(() => {
    switch (settings.navColor) {
      case 'integrate':
        return {
          layout: {
            // Dabang's drawer is a plain white `Paper`. It carries no right-hand border at all;
            // ours keeps the divider, for the same reason the app bar keeps its hairline.
            '--layout-nav-bg': palette.background.paper,
            '--layout-nav-horizontal-bg': varAlpha(palette.background.defaultChannel, 0.8),
            '--layout-nav-border-color': palette.divider,
            '--layout-nav-text-primary-color': palette.text.primary,
            '--layout-nav-text-secondary-color': palette.text.secondary,
            '--layout-nav-text-disabled-color': palette.text.disabled,
            [stylesMode.dark]: {
              '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], 0.08),
              '--layout-nav-horizontal-bg': varAlpha(palette.background.defaultChannel, 0.96),
            },
          },
          section: {
            ...NAV_SHAPE_VARS,
            // The selected entry is filled solid, so its label and icon go white. #5D5FEF holds
            // white at 4.78:1, which is why this works as a fill and Mantis's blue wouldn't.
            '--nav-item-root-active-bg': palette.primary.main,
            '--nav-item-root-active-hover-bg': palette.primary.dark,
            '--nav-item-root-active-color': palette.common.white,
            '--nav-item-root-active-color-on-dark': palette.common.white,
          },
        };
      case 'apparent':
        return {
          layout: {
            '--layout-nav-bg': palette.grey[900],
            '--layout-nav-horizontal-bg': varAlpha(palette.grey['900Channel'], 0.96),
            '--layout-nav-border-color': 'transparent',
            '--layout-nav-text-primary-color': palette.common.white,
            '--layout-nav-text-secondary-color': palette.grey[500],
            '--layout-nav-text-disabled-color': palette.grey[600],
            [stylesMode.dark]: {
              '--layout-nav-bg': palette.grey[800],
              '--layout-nav-horizontal-bg': varAlpha(palette.grey['800Channel'], 0.8),
            },
          },
          section: {
            ...NAV_SHAPE_VARS,
            // caption
            '--nav-item-caption-color': palette.grey[600],
            // subheader
            '--nav-subheader-color': palette.grey[600],
            '--nav-subheader-hover-color': palette.common.white,
            // item
            '--nav-item-color': palette.grey[500],
            '--nav-item-root-active-color': palette.primary.light,
            '--nav-item-root-open-color': palette.common.white,
            // bullet
            '--nav-bullet-light-color': bulletColor.dark,
            // sub
            ...(settings.navLayout === 'vertical' && {
              '--nav-item-sub-active-color': palette.common.white,
              '--nav-item-sub-open-color': palette.common.white,
            }),
          },
        };
      default:
        throw new Error(`Invalid color: ${settings.navColor}`);
    }
  }, [
    palette.background.default,
    palette.background.defaultChannel,
    palette.common.white,
    palette.grey,
    palette.primary.dark,
    palette.primary.light,
    palette.primary.main,
    palette.text.disabled,
    palette.text.primary,
    palette.text.secondary,
    settings.navColor,
    settings.navLayout,
  ]);
}
